/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

/**
 * Generate a unique ID without uuid dependency
 */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Character data structure:
 * - id: unique identifier
 * - projectId: associated project ID
 * - fullName: full name of the character
 * - displayName: display name (defaults to fullName)
 * - role: role in story (Protagonist, Antagonist, Supporting, Minor)
 * - age: age
 * - race: race/ethnicity
 * - hairColor: hair color
 * - eyeColor: eye color
 * - sexuality: sexuality
 * - personality: short summary
 * - notes: free-form rich text or markdown
 * - tags: array of optional tags (traits, roles, status)
 * - familyId: nullable family ID
 * - createdAt: creation timestamp
 * - updatedAt: last update timestamp
 */

/**
 * Family data structure:
 * - id: unique identifier
 * - projectId: associated project ID
 * - familyName: display name (renamable, not locked to last name)
 * - members: array of character IDs
 * - connections: array of relationship objects { from: characterId, to: characterId, type: 'parent'|'sibling'|'spouse'|etc }
 * - createdAt: creation timestamp
 * - updatedAt: last update timestamp
 */

const initialState = {
  characters: [],
  families: [],
  activeCharacterId: null,
  activeFamilyId: null,
};

export const charactersSlice = createSlice({
  name: 'characters',
  initialState,
  reducers: {
    // Character CRUD
    addCharacter: (state, action) => {
      const character = action.payload;
      const familyId = character.familyId || null;

      const newCharacter = {
        id: character.id || generateId(),
        projectId: character.projectId || null,
        fullName: character.fullName || '',
        displayName: character.displayName || character.fullName || 'Unnamed Character',
        role: character.role || '',
        age: character.age || null,
        race: character.race || '',
        hairColor: character.hairColor || '',
        eyeColor: character.eyeColor || '',
        sexuality: character.sexuality || '',
        personality: character.personality || '',
        notes: character.notes || '',
        tags: character.tags || [],
        familyId,
        createdAt: character.createdAt || Date.now(),
        updatedAt: Date.now(),
      };

      state.characters.push(newCharacter);

      // Add character to family if familyId exists
      if (familyId) {
        const family = state.families.find((f) => f.id === familyId);
        if (family && !family.members.includes(newCharacter.id)) {
          family.members.push(newCharacter.id);
          family.updatedAt = Date.now();
        }
      }
    },

    setCharacters: (state, action) => {
      state.characters = action.payload;
    },

    updateCharacter: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.characters.findIndex((c) => c.id === id);
      if (index !== -1) {
        const updatedCharacter = {
          ...state.characters[index],
          ...updates,
          updatedAt: Date.now(),
        };

        // Update displayName if fullName changed
        if (updates.fullName !== undefined) {
          updatedCharacter.displayName = updates.fullName.trim() || 'Unnamed Character';
        }

        // Handle family changes if familyId changed
        const oldCharacter = state.characters[index];
        if (updates.familyId !== undefined && updates.familyId !== oldCharacter.familyId) {
          // Remove from old family
          if (oldCharacter.familyId) {
            const oldFamily = state.families.find((f) => f.id === oldCharacter.familyId);
            if (oldFamily) {
              oldFamily.members = oldFamily.members.filter((mId) => mId !== id);
              if (oldFamily.members.length === 0) {
                // Remove empty family
                state.families = state.families.filter((f) => f.id !== oldCharacter.familyId);
              } else {
                oldFamily.updatedAt = Date.now();
              }
            }
          }

          // Add to new family
          if (updates.familyId) {
            const newFamily = state.families.find((f) => f.id === updates.familyId);
            if (newFamily && !newFamily.members.includes(id)) {
              newFamily.members.push(id);
              newFamily.updatedAt = Date.now();
            }
          }
        }

        state.characters[index] = updatedCharacter;
      }
    },

    removeCharacter: (state, action) => {
      const characterId = action.payload;
      const character = state.characters.find((c) => c.id === characterId);

      if (character) {
        // Remove from family
        if (character.familyId) {
          const family = state.families.find((f) => f.id === character.familyId);
          if (family) {
            family.members = family.members.filter((mId) => mId !== characterId);
            // Remove connections involving this character
            family.connections = family.connections.filter(
              (conn) => conn.from !== characterId && conn.to !== characterId
            );

            if (family.members.length === 0) {
              // Remove empty family
              state.families = state.families.filter((f) => f.id !== character.familyId);
            } else {
              family.updatedAt = Date.now();
            }
          }
        }
      }

      state.characters = state.characters.filter((c) => c.id !== characterId);
      if (state.activeCharacterId === characterId) {
        state.activeCharacterId = null;
      }
    },

    setActiveCharacter: (state, action) => {
      state.activeCharacterId = action.payload;
    },

    // Family CRUD
    addFamily: (state, action) => {
      const family = action.payload;
      const newFamily = {
        id: family.id || generateId(),
        projectId: family.projectId || null,
        familyName: family.familyName || 'Unnamed Family',
        members: family.members || [],
        connections: family.connections || [],
        createdAt: family.createdAt || Date.now(),
        updatedAt: Date.now(),
      };
      state.families.push(newFamily);
    },

    setFamilies: (state, action) => {
      state.families = action.payload;
    },

    updateFamily: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.families.findIndex((f) => f.id === id);
      if (index !== -1) {
        state.families[index] = {
          ...state.families[index],
          ...updates,
          updatedAt: Date.now(),
        };
      }
    },

    removeFamily: (state, action) => {
      const familyId = action.payload;
      const family = state.families.find((f) => f.id === familyId);

      if (family) {
        // Remove familyId from all characters in this family
        family.members.forEach((characterId) => {
          const character = state.characters.find((c) => c.id === characterId);
          if (character) {
            character.familyId = null;
            character.updatedAt = Date.now();
          }
        });
      }

      state.families = state.families.filter((f) => f.id !== familyId);
      if (state.activeFamilyId === familyId) {
        state.activeFamilyId = null;
      }
    },

    setActiveFamily: (state, action) => {
      state.activeFamilyId = action.payload;
    },

    // Family member operations
    addMemberToFamily: (state, action) => {
      const { familyId, characterId } = action.payload;
      const family = state.families.find((f) => f.id === familyId);
      const character = state.characters.find((c) => c.id === characterId);

      if (family && character) {
        if (!family.members.includes(characterId)) {
          family.members.push(characterId);
          family.updatedAt = Date.now();
        }
        character.familyId = familyId;
        character.updatedAt = Date.now();
      }
    },

    removeMemberFromFamily: (state, action) => {
      const { familyId, characterId } = action.payload;
      const family = state.families.find((f) => f.id === familyId);
      const character = state.characters.find((c) => c.id === characterId);

      if (family && character) {
        family.members = family.members.filter((mId) => mId !== characterId);
        family.connections = family.connections.filter(
          (conn) => conn.from !== characterId && conn.to !== characterId
        );
        family.updatedAt = Date.now();

        character.familyId = null;
        character.updatedAt = Date.now();

        // Remove empty family
        if (family.members.length === 0) {
          state.families = state.families.filter((f) => f.id !== familyId);
        }
      }
    },

    // Family connection operations
    addConnection: (state, action) => {
      const { familyId, connection } = action.payload;
      const family = state.families.find((f) => f.id === familyId);

      if (family) {
        const newConnection = {
          id: connection.id || generateId(),
          from: connection.from,
          to: connection.to,
          type: connection.type || 'related',
        };

        // Check if connection already exists (bidirectional check)
        const exists = family.connections.some(
          (conn) =>
            (conn.from === newConnection.from && conn.to === newConnection.to) ||
            (conn.from === newConnection.to && conn.to === newConnection.from)
        );

        if (!exists) {
          family.connections.push(newConnection);
          family.updatedAt = Date.now();
        }
      }
    },

    updateConnection: (state, action) => {
      const { familyId, connectionId, updates } = action.payload;
      const family = state.families.find((f) => f.id === familyId);

      if (family) {
        const connectionIndex = family.connections.findIndex((c) => c.id === connectionId);
        if (connectionIndex !== -1) {
          family.connections[connectionIndex] = {
            ...family.connections[connectionIndex],
            ...updates,
          };
          family.updatedAt = Date.now();
        }
      }
    },

    removeConnection: (state, action) => {
      const { familyId, connectionId } = action.payload;
      const family = state.families.find((f) => f.id === familyId);

      if (family) {
        family.connections = family.connections.filter((c) => c.id !== connectionId);
        family.updatedAt = Date.now();
      }
    },
  },
});

export const {
  addCharacter,
  setCharacters,
  updateCharacter,
  removeCharacter,
  setActiveCharacter,
  addFamily,
  setFamilies,
  updateFamily,
  removeFamily,
  setActiveFamily,
  addMemberToFamily,
  removeMemberFromFamily,
  addConnection,
  updateConnection,
  removeConnection,
} = charactersSlice.actions;

// Selectors
export const selectCharacters = (state) => state.characters.characters;
export const selectFamilies = (state) => state.characters.families;
export const selectActiveCharacterId = (state) => state.characters.activeCharacterId;
export const selectActiveFamilyId = (state) => state.characters.activeFamilyId;
export const selectCharacterById = (id) => (state) =>
  state.characters.characters.find((c) => c.id === id);
export const selectFamilyById = (id) => (state) =>
  state.characters.families.find((f) => f.id === id);
export const selectCharactersByProject = (projectId) => (state) =>
  state.characters.characters.filter((c) => c.projectId === projectId);
export const selectFamiliesByProject = (projectId) => (state) =>
  state.characters.families.filter((f) => f.projectId === projectId);
export const selectCharactersByFamily = (familyId) => (state) =>
  state.characters.characters.filter((c) => c.familyId === familyId);

export default charactersSlice.reducer;
